import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import PageTransition from "../components/PageTransition";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Glass input wrapper component
const GlassInputWrapper = ({ children }) => (
  <div className="rounded-2xl border border-border bg-foreground/5 backdrop-blur-sm transition-colors focus-within:border-violet-400/70 focus-within:bg-violet-500/10">
    {children}
  </div>
);

// Testimonial card component
const TestimonialCard = ({ testimonial, delay }) => (
  <div className={`animate-testimonial ${delay} flex items-start gap-3 rounded-3xl bg-card/40 dark:bg-zinc-800/40 backdrop-blur-xl border border-white/10 p-5 w-64`}>
    <img src={testimonial.avatarSrc} className="h-10 w-10 object-cover rounded-2xl" alt="avatar" />
    <div className="text-sm leading-snug">
      <p className="flex items-center gap-1 font-medium">{testimonial.name}</p>
      <p className="text-muted-foreground">{testimonial.handle}</p>
      <p className="mt-1 text-foreground/80">{testimonial.text}</p>
    </div>
  </div>
);

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [autoReply, setAutoReply] = useState(null);
  const [submittedName, setSubmittedName] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Subject validation
    if (!formData.subject) {
      errors.subject = "Please select a subject";
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters long";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      subject: value
    }));

    // Clear field error when user selects
    if (fieldErrors.subject) {
      setFieldErrors(prev => ({
        ...prev,
        subject: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Generate a ticket ID
      const ticketId = `TKT${Date.now()}${Math.random().toString(36).substr(2, 9)}`.toUpperCase();

      // Add document to Firestore contacts collection
      await addDoc(collection(db, "contacts"), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        ticketId: ticketId,
        createdAt: serverTimestamp(),
        status: "new",
        replied: false
      });

      // Create auto-reply data
      const autoReplyData = {
        message: "Thank you for reaching out! Our team has received your message and will get back to you soon.",
        expectedResponseTime: "Within 24 hours",
        ticketId: ticketId
      };

      setSuccess(true);
      setAutoReply(autoReplyData);
      setSubmittedName(formData.name);
      setFormData({ name: "", email: "", subject: "", message: "" });
      setFieldErrors({});

    } catch (error) {
      console.error("Error submitting contact form:", error);
      setError(error.message || "Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Testimonials for the right side
  const testimonials = [
    {
      avatarSrc: "https://randomuser.me/api/portraits/women/44.jpg",
      name: "Maria Rodriguez",
      handle: "@mariabiz",
      text: "Their support team is incredible! Got my issue resolved in minutes, not hours."
    },
    {
      avatarSrc: "https://randomuser.me/api/portraits/men/46.jpg",
      name: "Ahmed Hassan",
      handle: "@ahmedtech",
      text: "Best customer service I've experienced. The team really cares about their users."
    },
    {
      avatarSrc: "https://randomuser.me/api/portraits/women/68.jpg",
      name: "Lisa Wong",
      handle: "@lisawong",
      text: "Quick responses and helpful solutions. This is how support should be done!"
    },
  ];

  return (
    <PageTransition>
      <div className="h-[100dvh] flex flex-col md:flex-row font-geist w-[100dvw]">
        {/* Left column: contact form */}
        <section className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
          <div className="w-full max-w-md">
            {success ? (
              <div className="animate-element animate-delay-100 text-center space-y-6">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-foreground">Thank you, {submittedName || 'there'}!</h3>
                  <p className="text-muted-foreground">Your message has been received successfully.</p>
                  
                  {autoReply && (
                    <div className="bg-gradient-to-r from-primary/5 to-accent/10 border border-primary/20 rounded-2xl p-6 text-left">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.7l-3.686.734.734-3.686A9.013 9.013 0 0112 3c4.97 0 9 4.03 9 9z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-2">What happens next?</h4>
                          <p className="text-muted-foreground mb-3">{autoReply.message}</p>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p><span className="font-medium">Expected response time:</span> {autoReply.expectedResponseTime}</p>
                            <p><span className="font-medium">Ticket ID:</span> #{autoReply.ticketId.slice(-8).toUpperCase()}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setAutoReply(null);
                  }}
                  className="text-violet-400 hover:text-violet-300 font-medium transition-colors underline-offset-4 hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <h1 className="animate-element animate-delay-100 text-4xl md:text-5xl font-semibold leading-tight">
                  <span className="font-light text-foreground tracking-tighter">Get in touch</span>
                </h1>
                <p className="animate-element animate-delay-200 text-muted-foreground">
                  Have a question or need help? We're here for you
                </p>

                <form className="space-y-5" onSubmit={handleSubmit}>
                  {/* Name field */}
                  <div className="animate-element animate-delay-300">
                    <label className="text-sm font-medium text-muted-foreground">Full Name *</label>
                    <GlassInputWrapper>
                      <input
                        name="name"
                        type="text"
                        placeholder="Enter your full name"
                        className="w-full bg-transparent text-sm p-4 rounded-2xl focus:outline-none"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </GlassInputWrapper>
                    {fieldErrors.name && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.name}</p>
                    )}
                  </div>

                  {/* Email field */}
                  <div className="animate-element animate-delay-400">
                    <label className="text-sm font-medium text-muted-foreground">Email Address *</label>
                    <GlassInputWrapper>
                      <input
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        className="w-full bg-transparent text-sm p-4 rounded-2xl focus:outline-none"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </GlassInputWrapper>
                    {fieldErrors.email && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.email}</p>
                    )}
                  </div>

                  {/* Subject field */}
                  <div className="animate-element animate-delay-500">
                    <label className="text-sm font-medium text-muted-foreground">Subject *</label>
                    <Select
                      value={formData.subject}
                      onValueChange={handleSelectChange}
                      disabled={isLoading}
                      required
                    >
                      <SelectTrigger className="w-full h-auto p-4 rounded-2xl border border-border bg-foreground/5 backdrop-blur-sm transition-colors focus:border-violet-400/70 focus:bg-violet-500/10 text-sm">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="support">Support</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {fieldErrors.subject && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.subject}</p>
                    )}
                  </div>

                  {/* Message field */}
                  <div className="animate-element animate-delay-600">
                    <label className="text-sm font-medium text-muted-foreground">Message *</label>
                    <GlassInputWrapper>
                      <textarea
                        name="message"
                        rows={5}
                        placeholder="Tell us how we can help you... (minimum 10 characters)"
                        className="w-full bg-transparent text-sm p-4 rounded-2xl focus:outline-none resize-none"
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </GlassInputWrapper>
                    {fieldErrors.message && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.message}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formData.message.length}/10 characters minimum
                    </p>
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="animate-element animate-delay-700 w-full rounded-2xl bg-primary py-4 font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Sending..." : "Send Message"}
                  </button>
                </form>

                {/* Error message */}
                {error && (
                  <div className="animate-element rounded-2xl bg-destructive/10 border border-destructive/20 p-4">
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 shrink-0 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-destructive">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Right column: hero image + testimonials */}
        <section className="hidden md:block flex-1 relative p-4">
          <div 
            className="animate-slide-right animate-delay-300 absolute inset-4 rounded-3xl bg-cover bg-center" 
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1642615835477-d303d7dc9ee9?w=2160&q=80)` 
            }}
          ></div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4 px-8 w-full justify-center">
            <TestimonialCard testimonial={testimonials[0]} delay="animate-delay-1000" />
            <div className="hidden xl:flex">
              <TestimonialCard testimonial={testimonials[1]} delay="animate-delay-1200" />
            </div>
            <div className="hidden 2xl:flex">
              <TestimonialCard testimonial={testimonials[2]} delay="animate-delay-1400" />
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default ContactUs;